import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Grid date: 3-letter month; omit year when it is the current year (e.g. "08 May" or "08 May 2025"). */
export function formatGridDate(value: Date | string | number | null | undefined, fallback = "-"): string {
  if (value === null || value === undefined || value === "") return fallback;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  const day = String(date.getDate()).padStart(2, "0");
  const month = MONTH_ABBR[date.getMonth()];
  const year = date.getFullYear();

  if (year === new Date().getFullYear()) {
    return `${day} ${month}`;
  }

  return `${day} ${month} ${year}`;
}

export function todayInputDate(): string {
  const d = new Date();
  return toLocalInputDate(d);
}

function toLocalInputDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDaysInputDate(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toLocalInputDate(d);
}

export function toInputDate(value: Date | string | null | undefined): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return toLocalInputDate(d);
}

/** Monday-start week containing the given date (local). */
export function getWeekRange(anchor: Date | string = new Date()): { from: string; to: string } {
  const date = typeof anchor === "string" ? new Date(`${anchor}T00:00:00`) : new Date(anchor);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay(); // 0 = Sun
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(date);
  monday.setDate(date.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: toLocalInputDate(monday), to: toLocalInputDate(sunday) };
}

export function getMonthRange(anchor: Date | string = new Date()): { from: string; to: string } {
  const date = typeof anchor === "string" ? new Date(`${anchor}T00:00:00`) : new Date(anchor);
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: toLocalInputDate(from), to: toLocalInputDate(to) };
}

export function getYearRange(anchor: Date | string = new Date()): { from: string; to: string } {
  const date = typeof anchor === "string" ? new Date(`${anchor}T00:00:00`) : new Date(anchor);
  const year = date.getFullYear();
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export type PeriodMode = "week" | "month" | "year";

export function getPeriodRange(mode: PeriodMode, anchor: Date | string = new Date()): { from: string; to: string } {
  if (mode === "month") return getMonthRange(anchor);
  if (mode === "year") return getYearRange(anchor);
  return getWeekRange(anchor);
}

export function shiftPeriodRange(mode: PeriodMode, from: string, delta: number): { from: string; to: string } {
  const date = new Date(`${from}T00:00:00`);
  if (mode === "month") {
    date.setMonth(date.getMonth() + delta);
    return getMonthRange(date);
  }
  if (mode === "year") {
    date.setFullYear(date.getFullYear() + delta);
    return getYearRange(date);
  }
  date.setDate(date.getDate() + delta * 7);
  return getWeekRange(date);
}

export function formatPeriodRangeLabel(mode: PeriodMode, from: string, to: string): string {
  if (mode === "year") {
    return String(new Date(`${from}T00:00:00`).getFullYear());
  }
  if (mode === "month") {
    const d = new Date(`${from}T00:00:00`);
    const month = MONTH_ABBR[d.getMonth()];
    const year = d.getFullYear();
    return year === new Date().getFullYear() ? month : `${month} ${year}`;
  }
  const fromDate = new Date(`${from}T00:00:00`);
  const toDate = new Date(`${to}T00:00:00`);
  const currentYear = new Date().getFullYear();
  const showFromYear = fromDate.getFullYear() !== currentYear || fromDate.getFullYear() !== toDate.getFullYear();
  const showToYear = toDate.getFullYear() !== currentYear || fromDate.getFullYear() !== toDate.getFullYear();
  const fromLabel = `${String(fromDate.getDate()).padStart(2, "0")} ${MONTH_ABBR[fromDate.getMonth()]}${showFromYear ? ` ${fromDate.getFullYear()}` : ""}`;
  const toLabel = `${String(toDate.getDate()).padStart(2, "0")} ${MONTH_ABBR[toDate.getMonth()]}${showToYear ? ` ${toDate.getFullYear()}` : ""}`;
  return `${fromLabel} – ${toLabel}`;
}

export function shiftWeekRange(from: string, weeks: number): { from: string; to: string } {
  return getWeekRange(addDaysInputDate(from, weeks * 7));
}

export function formatWeekRangeLabel(from: string, to: string): string {
  return `${formatGridDate(from)} – ${formatGridDate(to)}`;
}
